import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { vocabulary as vocabSection, flattenVocab, type VocabValues } from "@klubora/shared";
import { useTenantSettings } from "../../hooks/useTenantSettings";
import { usePublishedSendFailed } from "../../hooks/usePublishedSendFailed";
import { useAuditEmails } from "../../hooks/useAuditEmails";
import { writeTenantSetting } from "../../lib/writeTenantSetting";
import {
  choiceMessage,
  retryFailedSend,
  saveAffordance,
  shouldReplaceForm,
  writeForChoice,
  type VocabularyWriteCall,
} from "../../lib/vocabularyPublishChoice";
import { ConfirmDialog } from "../ConfirmDialog";
import { TextField } from "./TextField";
import { AuditFootnote } from "./AuditFootnote";
import { colors, fontSize, fontWeight, radius, shadow, space } from "../../lib/tokens";

interface Props { tenantId: string; }

const NAME_TRIPLE_KEYS: ReadonlyArray<keyof VocabValues> = [
  "member", "prospect", "activity", "space", "booking",
  "benefit", "feed", "tier", "interest", "referral", "invite", "staff",
];

const TRIPLE_LABELS: Record<string, string> = {
  singular: "Singular", plural: "Plural", cta_add: "CTA — adicionar novo",
};

export function VocabularyEditor({ tenantId }: Props) {
  const { values, audit, loading } = useTenantSettings(tenantId, "vocabulary");
  const sendFailed = usePublishedSendFailed(tenantId);
  const auditUserIds = useMemo(
    () => Object.values(audit).map((a) => a.updated_by).filter((x): x is string => !!x),
    [audit],
  );
  const emails = useAuditEmails(auditUserIds);

  const [form, setForm] = useState<VocabValues | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [choiceOpen, setChoiceOpen] = useState(false);
  const blockRemoteForm = useRef(false);

  useEffect(() => {
    if (loading) return;
    if (!shouldReplaceForm(blockRemoteForm.current)) return;
    setForm(values);
  }, [loading, values]);

  const dirtyFlat = useMemo(() => {
    if (!form) return {};
    const before = flattenVocab(values);
    const after = flattenVocab(form);
    const out: Record<string, string> = {};
    for (const k of Object.keys(after)) {
      if (after[k] !== before[k]) out[k] = after[k];
    }
    return out;
  }, [form, values]);

  const isDirty = Object.keys(dirtyFlat).length > 0;
  const affordance = saveAffordance(isDirty, sendFailed);
  const saveDisabled = affordance === "disabled" || saving;

  const applyWrite = useCallback(async (call: VocabularyWriteCall) => {
    setSaving(true);
    setError(null);
    try {
      await writeTenantSetting(tenantId, "vocabulary", call.values, { publish: call.publish });
      blockRemoteForm.current = false;
      setChoiceOpen(false);
    } catch (e) {
      blockRemoteForm.current = true;
      setError(e instanceof Error ? e.message : String(e));
      setChoiceOpen(false);
    } finally {
      setSaving(false);
    }
  }, [tenantId]);

  const onSave = () => {
    if (affordance === "choice") {
      setChoiceOpen(true);
      return;
    }
    if (affordance === "retry") void applyWrite(retryFailedSend());
  };

  const onKeep = () => {
    const call = writeForChoice("keep", dirtyFlat);
    if (call) void applyWrite(call);
  };

  const onSend = () => {
    const call = writeForChoice("send", dirtyFlat);
    if (call) void applyWrite(call);
  };

  if (loading || !form) {
    return <div style={{ color: colors.stone, padding: space.lg }}>Carregando...</div>;
  }

  const setLeaf = (top: string, leaf: string, next: string) => {
    setForm((prev) => prev ? ({
      ...prev,
      [top]: { ...(prev[top as keyof VocabValues] as Record<string, string>), [leaf]: next },
    }) as VocabValues : prev);
  };

  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: space.lg, marginBottom: space.xl }}>
        {NAME_TRIPLE_KEYS.map((topKey) => {
          const triple = form[topKey] as { singular: string; plural: string; cta_add: string; gender: "m" | "f" };
          return (
            <article key={topKey as string}
                     style={{ background: colors.white, border: `1px solid ${colors.fog}`,
                              borderRadius: radius.md, boxShadow: shadow.sm, padding: space.lg }}>
              <h4 style={{ fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: colors.ink, marginBottom: space.md }}>
                {vocabSection.meta[topKey].label}
              </h4>
              {(["singular", "plural", "cta_add"] as const).map((leaf) => (
                <div key={leaf}>
                  <TextField label={TRIPLE_LABELS[leaf]}
                             value={triple[leaf]}
                             onChange={(next) => setLeaf(topKey as string, leaf, next)} />
                  <AuditFootnote audit={audit[`${topKey as string}.${leaf}`]} emails={emails} />
                </div>
              ))}
              <div style={{ marginTop: space.sm }}>
                <div style={{ fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: colors.ink, marginBottom: space.xs }}>
                  Gênero
                </div>
                <div style={{ display: "flex", gap: space.md }}>
                  {(["m", "f"] as const).map((g) => (
                    <label key={g} style={{ display: "flex", alignItems: "center", gap: space.xs, cursor: "pointer", fontSize: fontSize.sm, color: colors.ink }}>
                      <input type="radio" name={`${topKey as string}-gender`}
                             checked={triple.gender === g}
                             onChange={() => setLeaf(topKey as string, "gender", g)} />
                      {g === "m" ? "Masculino (Novo)" : "Feminino (Nova)"}
                    </label>
                  ))}
                </div>
                <AuditFootnote audit={audit[`${topKey as string}.gender`]} emails={emails} />
              </div>
            </article>
          );
        })}

        <article style={{ background: colors.white, border: `1px solid ${colors.fog}`,
                          borderRadius: radius.md, boxShadow: shadow.sm, padding: space.lg }}>
          <h4 style={{ fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: colors.ink, marginBottom: space.md }}>
            {vocabSection.meta.section.label}
          </h4>
          {(["content", "club"] as const).map((leaf) => (
            <div key={leaf}>
              <TextField label={leaf === "content" ? "Aba de conteúdo" : "Aba de clube"}
                         value={form.section[leaf]}
                         onChange={(next) => setLeaf("section", leaf, next)} />
              <AuditFootnote audit={audit[`section.${leaf}`]} emails={emails} />
            </div>
          ))}
        </article>
      </div>

      {error && (
        <div style={{ marginBottom: space.md, padding: space.md, background: colors.errorSoft,
                      color: colors.error, borderRadius: radius.sm, fontSize: fontSize.md }}>
          {error}
        </div>
      )}

      <div style={{ display: "flex", gap: space.sm, justifyContent: "flex-end" }}>
        <button type="button" onClick={() => { blockRemoteForm.current = false; setForm(values); }} disabled={!isDirty || saving}
                style={{ height: 36, padding: "0 16px", background: colors.white, color: colors.ink,
                         border: `1px solid ${colors.fog}`, borderRadius: radius.sm,
                         fontSize: fontSize.md, fontWeight: fontWeight.medium,
                         cursor: !isDirty || saving ? "not-allowed" : "pointer",
                         opacity: !isDirty || saving ? 0.5 : 1 }}>
          Cancelar
        </button>
        <button type="button" onClick={onSave} disabled={saveDisabled}
                style={{ height: 36, padding: "0 16px", background: colors.ink, color: colors.paper,
                         border: "none", borderRadius: radius.sm,
                         fontSize: fontSize.md, fontWeight: fontWeight.medium,
                         cursor: saveDisabled ? "not-allowed" : "pointer",
                         opacity: saveDisabled ? 0.5 : 1 }}>
          {saving ? "Salvando..." : affordance === "retry" ? "Reenviar ao app" : "Salvar"}
        </button>
      </div>

      <ConfirmDialog
        open={choiceOpen}
        title="Enviar estas palavras ao app?"
        message={choiceMessage(dirtyFlat)}
        cancelLabel="Cancelar"
        secondaryLabel="Não enviar"
        onSecondary={onKeep}
        confirmLabel="Enviar ao app"
        loading={saving}
        onConfirm={onSend}
        onCancel={() => setChoiceOpen(false)}
      />
    </div>
  );
}
